import { httpClient } from "@/lib/http";
import {
    RIDE_OFFERS_ENDPOINTS,
    BOOKINGS_ENDPOINTS,
    TRIPS_ENDPOINTS,
} from "@/config/api";
import type { ApiResponse } from "@/lib/http";
import type {
    RideOffersQueryParams,
    RideOffersResponse,
    RideOfferDetails,
    CancelRideOfferResponse,
    SuspendRideOfferResponse,
    BookingsQueryParams,
    BookingsResponse,
    BookingDetails,
    CancelBookingResponse,
    RefundBookingResponse,
    TripsQueryParams,
    TripsResponse,
    TripDetails,
    CancelTripResponse,
    ForceCompleteTripResponse,
} from "../types";

// ============================================================================
// Helper Functions
// ============================================================================

const buildRideOffersQuery = (params: RideOffersQueryParams): string => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append("page", params.page.toString());
    if (params.limit !== undefined) q.append("limit", params.limit.toString());
    if (params.status) q.append("status", params.status);
    if (params.driverId) q.append("driverId", params.driverId);
    if (params.vehicleType) q.append("vehicleType", params.vehicleType);
    if (params.dateFrom) q.append("dateFrom", params.dateFrom);
    if (params.dateTo) q.append("dateTo", params.dateTo);
    if (params.search) q.append("search", params.search);
    return q.toString();
};

const buildBookingsQuery = (params: BookingsQueryParams): string => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append("page", params.page.toString());
    if (params.limit !== undefined) q.append("limit", params.limit.toString());
    if (params.status) q.append("status", params.status);
    if (params.paymentStatus) q.append("paymentStatus", params.paymentStatus);
    if (params.riderId) q.append("riderId", params.riderId);
    if (params.rideOfferId) q.append("rideOfferId", params.rideOfferId);
    if (params.dateFrom) q.append("dateFrom", params.dateFrom);
    if (params.dateTo) q.append("dateTo", params.dateTo);
    if (params.search) q.append("search", params.search);
    return q.toString();
};

const buildTripsQuery = (params: TripsQueryParams): string => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append("page", params.page.toString());
    if (params.limit !== undefined) q.append("limit", params.limit.toString());
    if (params.status) q.append("status", params.status);
    if (params.driverId) q.append("driverId", params.driverId);
    if (params.passengerId) q.append("passengerId", params.passengerId);
    if (params.rideOfferId) q.append("rideOfferId", params.rideOfferId);
    if (params.dateFrom) q.append("dateFrom", params.dateFrom);
    if (params.dateTo) q.append("dateTo", params.dateTo);
    if (params.hasDriverRating !== undefined && params.hasDriverRating !== "")
        q.append("hasDriverRating", params.hasDriverRating.toString());
    if (params.hasPassengerRating !== undefined && params.hasPassengerRating !== "")
        q.append("hasPassengerRating", params.hasPassengerRating.toString());
    return q.toString();
};

// ============================================================================
// Ride Offers API
// ============================================================================

export const getRideOffers = async (params: RideOffersQueryParams = {}) => {
    const qs = buildRideOffersQuery(params);
    const url = qs ? `${RIDE_OFFERS_ENDPOINTS.LIST}?${qs}` : RIDE_OFFERS_ENDPOINTS.LIST;
    const { data } = await httpClient.get<ApiResponse<RideOffersResponse>>(url);
    return data;
};

export const getRideOfferDetails = async (rideOfferId: string) => {
    const { data } = await httpClient.get<ApiResponse<RideOfferDetails>>(
        RIDE_OFFERS_ENDPOINTS.DETAILS(rideOfferId)
    );
    return data;
};

export const cancelRideOffer = async (rideOfferId: string, reason: string) => {
    const { data } = await httpClient.post<ApiResponse<CancelRideOfferResponse>>(
        RIDE_OFFERS_ENDPOINTS.CANCEL(rideOfferId),
        { reason }
    );
    return data;
};

export const suspendRideOffer = async (
    rideOfferId: string,
    suspend: boolean,
    reason?: string
) => {
    const { data } = await httpClient.post<ApiResponse<SuspendRideOfferResponse>>(
        RIDE_OFFERS_ENDPOINTS.SUSPEND(rideOfferId),
        { suspend, reason }
    );
    return data;
};

// ============================================================================
// Bookings API
// ============================================================================

export const getBookings = async (params: BookingsQueryParams = {}) => {
    const qs = buildBookingsQuery(params);
    const url = qs ? `${BOOKINGS_ENDPOINTS.LIST}?${qs}` : BOOKINGS_ENDPOINTS.LIST;
    const { data } = await httpClient.get<ApiResponse<BookingsResponse>>(url);
    return data;
};

export const getBookingDetails = async (bookingId: string) => {
    const { data } = await httpClient.get<ApiResponse<BookingDetails>>(
        BOOKINGS_ENDPOINTS.DETAILS(bookingId)
    );
    return data;
};

export const cancelBooking = async (bookingId: string, reason: string) => {
    const { data } = await httpClient.post<ApiResponse<CancelBookingResponse>>(
        BOOKINGS_ENDPOINTS.CANCEL(bookingId),
        { reason }
    );
    return data;
};

export const refundBooking = async (
    bookingId: string,
    reason?: string,
    amount?: number
) => {
    const body: { reason?: string; amount?: number } = {};
    if (reason) body.reason = reason;
    if (amount !== undefined) body.amount = amount;
    const { data } = await httpClient.post<ApiResponse<RefundBookingResponse>>(
        BOOKINGS_ENDPOINTS.REFUND(bookingId),
        body
    );
    return data;
};

// ============================================================================
// Trips API
// ============================================================================

export const getTrips = async (params: TripsQueryParams = {}) => {
    const qs = buildTripsQuery(params);
    const url = qs ? `${TRIPS_ENDPOINTS.LIST}?${qs}` : TRIPS_ENDPOINTS.LIST;
    const { data } = await httpClient.get<ApiResponse<TripsResponse>>(url);
    return data;
};

export const getTripDetails = async (tripId: string) => {
    const { data } = await httpClient.get<ApiResponse<TripDetails>>(
        TRIPS_ENDPOINTS.DETAILS(tripId)
    );
    return data;
};

export const cancelTrip = async (tripId: string, reason: string) => {
    const { data } = await httpClient.post<ApiResponse<CancelTripResponse>>(
        TRIPS_ENDPOINTS.CANCEL(tripId),
        { reason }
    );
    return data;
};

export const forceCompleteTrip = async (
    tripId: string,
    reason?: string,
    finalFare?: number
) => {
    const body: { reason?: string; finalFare?: number } = {};
    if (reason) body.reason = reason;
    if (finalFare !== undefined) body.finalFare = finalFare;
    const { data } = await httpClient.post<ApiResponse<ForceCompleteTripResponse>>(
        TRIPS_ENDPOINTS.COMPLETE(tripId),
        body
    );
    return data;
};
